import React from 'react';

import { useFormikContext } from 'formik';

import SectionList from 'components/practice/UserPreferences/components/SectionList';
import { showAlert } from 'components/shared/Alert/Alert';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Input from 'components/shared/Input/Input';
import Switch from 'components/shared/Switch/Switch';

const OperationalSettings = ({ setShowTechnologyModal }) => {
  const { setFieldValue, values } = useFormikContext();

  const handleStickyNotesChange = (checked) => {
    const new_display_settings = {
      ...values?.displaySettings,
      sticky_notes: {
        alert: checked
      }
    };
    setFieldValue('displaySettings', new_display_settings);
    setFieldValue('stickyNotesAlert', checked);
  };

  const handleTechnologyFeeToggle = (checked) => {
    if (checked) {
      setShowTechnologyModal(true);
    } else {
      setFieldValue('surchargeEnabled', false);
    }
  };

  const handleFeePercentageChange = (e) => {
    const numValue = parseFloat(e.target.value) || 0;

    if (numValue > 6) {
      showAlert({
        title: 'Invalid Fee Percentage',
        message: 'Fee percentage cannot exceed 6%. Value will be set to 6%.',
        color: 'warning'
      });
    } else if (numValue < 0) {
      showAlert({
        title: 'Invalid Fee Percentage',
        message: 'Fee percentage cannot be negative. Value will be set to 0%.',
        color: 'warning'
      });
    }

    const limitedValue = Math.min(Math.max(numValue, 0), 6);
    setFieldValue('surchargeAmount', limitedValue * 100);
  };

  const Divider = () => <div className="h-[1px] bg-neutral-200" />;

  return (
    <>
      <SectionList
        title="Operational settings"
        description="Configure practice-specific options like technology fee, sticky notes, and batch time preferences.">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={values?.stickyNotesAlert}
              onChange={handleStickyNotesChange}
              id="sticky-notes-switch"
            />
            <label htmlFor="sticky-notes-switch" className="text-sm font-400 text-neutral-900">
              Automatically open sticky notes
            </label>
          </div>

          <Divider />

          {/* Technology fee */}
          <div className="flex items-center gap-2">
            <Switch
              checked={values?.surchargeEnabled}
              onChange={handleTechnologyFeeToggle}
              id="technology-fee-switch"
            />
            <label htmlFor="technology-fee-switch" className="text-sm font-400 text-neutral-900">
              Technology fee
            </label>
          </div>

          {/* Fee percentage - only show if technology fee is enabled */}
          {values?.surchargeEnabled && (
            <Input
              label="Fee percentage"
              value={(values?.surchargeAmount / 100).toFixed(1)}
              onChange={handleFeePercentageChange}
              min={0}
              max={6}
              type="number"
              className="max-w-[125px]"
              error={
                values?.surchargeAmount / 100 > 6
                  ? 'Fee cannot exceed 6%'
                  : values?.surchargeAmount / 100 < 0
                    ? 'Fee cannot be negative'
                    : null
              }
            />
          )}
        </div>
      </SectionList>

      <Divider />

      {/* Payment batch time section */}
      <SectionList
        title="Payment batch time"
        description="Daily cutoff for batching and transferring processed payments to your bank.">
        <div className=" flex flex-col gap-2">
          <label className="text-sm font-500 text-neutral-800">Transaction batch time</label>
          {/* For now this will be read only */}
          <Input value="07:00 AM, EST" disabled className="w-max" icon="clock-mono" />
        </div>
      </SectionList>
    </>
  );
};

export default withErrorBoundary(OperationalSettings);
